import { useRef } from 'react';
import type { Query, QueryClient } from '@tanstack/react-query';
import { api, call, type Schemas } from '../../api/client';
import { useQueryClient } from '../../api/query';
import { useTopicEvents } from '../../realtime/hooks';

type Action = Schemas['Action'];
type Header = Schemas['AccountHeader'];
type State = Schemas['AccountActionState'];
type Event = { kind?: string; action?: { id?: string; status?: string; server_id?: string; login?: number } };

/** Re-reads of the account after an executed action: the engine writes the account row a few seconds after the result. */
const SETTLE_MS = [1500, 4000, 8000, 15000, 30000];

/** Only a fresh result is patched in: an old one (a request reopened from history) may describe a state since changed. */
const FRESH_MS = 120_000;

/** Reads that show an account's state or derive from it (lists, groups, cases, previews). */
const DERIVED = [
  ['accounts'],
  ['account-positions'],
  ['account-timeline'],
  ['action-preview'],
  ['approvals'],
  ['cases'],
  ['case'],
  ['groups'],
  ['coordinated-group'],
  ['toxic'],
  ['traders'],
  ['exposure'],
  ['suggestions'],
  ['shell'],
];

/** Actions already handled (each settles once, however many pages report it). */
const settled = new Set<string>();
/** Per account, the execution time of the newest action settling: an older result never overrides a newer one. */
const latest = new Map<string, { at: number; id: string }>();

/** Header queries of one account, whatever their key shape (pages key GET /accounts/{login} differently). */
function headerQueries(qc: QueryClient, login: number, serverId: string) {
  return qc.getQueryCache().findAll({
    predicate: (q: Query) => {
      if (q.queryKey[0] !== 'account') return false;
      const h = q.state.data as Header | undefined;
      return (
        !!h &&
        typeof h === 'object' &&
        h.account?.login === login &&
        (!serverId || !h.account.server_id || h.account.server_id === serverId)
      );
    },
  });
}

const same = (h: Header, s: State) =>
  h.trading_enabled === s.trade_enabled && h.account_enabled === s.enabled && (!s.group || h.group === s.group);

const patch = (h: Header, after: State): Header => ({
  ...h,
  trading_enabled: after.trade_enabled,
  account_enabled: after.enabled,
  ...(after.group ? { group: after.group } : {}),
});

function invalidateHeaders(qc: QueryClient, login: number, serverId: string) {
  for (const q of headerQueries(qc, login, serverId)) void qc.invalidateQueries({ queryKey: q.queryKey, exact: true });
}

// derived reads refresh once per burst of results (a bulk request finishes many actions at once)
let derivedTimer: ReturnType<typeof setTimeout> | null = null;
function refreshDerived(qc: QueryClient) {
  if (derivedTimer) return;
  derivedTimer = setTimeout(() => {
    derivedTimer = null;
    for (const k of DERIVED) void qc.invalidateQueries({ queryKey: k });
  }, 300);
}

/**
 * Brings every open view of the account up to date with a finished action, without a reload. While
 * the server's account row still shows the state before the action, the platform's reported state
 * after it is shown instead; the account is read again until the row has moved (to that state, or
 * to a newer change, which then stands as read). If the re-reads run out, the patch is dropped and
 * the account read afresh. `nowMs` is the server's clock (freshness of the result).
 */
export function settleAction(qc: QueryClient, a: Action, nowMs = Date.now()) {
  if (settled.has(a.id)) return;
  settled.add(a.id);
  const login = a.account.login;
  const serverId = a.account.server_id || '';
  qc.setQueryData(['action', a.id], a);
  refreshDerived(qc);
  const at = Date.parse(a.result?.executed_at || '');
  const executed = a.result?.status === 'executed' || a.status === 'executed';
  const after = executed && (!Number.isFinite(at) || Math.abs(nowMs - at) < FRESH_MS) ? a.after : null;
  if (!after) {
    invalidateHeaders(qc, login, serverId);
    return;
  }
  const acct = serverId + ':' + login;
  const stamp = Number.isFinite(at) ? at : nowMs;
  if ((latest.get(acct)?.at ?? -Infinity) > stamp) return;
  latest.set(acct, { at: stamp, id: a.id });
  const current = () => latest.get(acct)?.id === a.id;
  const before = a.before;
  // the row has not caught up yet: it still shows the state before (or, unknown before, not the state after)
  const behind = (h: Header) => !same(h, after) && (before ? same(h, before) : true);
  const apply = (row?: Header) => {
    for (const q of headerQueries(qc, login, serverId)) {
      const h = row || (q.state.data as Header);
      qc.setQueryData(q.queryKey, behind(h) ? patch(h, after) : h);
    }
  };
  const finish = () => {
    if (current()) latest.delete(acct);
    refreshDerived(qc);
  };
  apply();
  let i = 0;
  const next = () => {
    if (!current()) return;
    if (i >= SETTLE_MS.length) {
      finish();
      invalidateHeaders(qc, login, serverId);
      return;
    }
    setTimeout(async () => {
      i++;
      try {
        const row = await call(api.GET('/accounts/{login}', { params: { path: { login }, query: { server_id: serverId || undefined } } }));
        if (!current()) return;
        apply(row);
        if (!behind(row)) return finish();
      } catch {
        /* the next re-read tries again */
      }
      next();
    }, SETTLE_MS[i]);
  };
  next();
}

/**
 * App-wide: every decision or result of an action (`cases` topic: approval.*, action.*) updates the
 * reads that depend on it on every page, so a page never needs a reload after an action.
 * `serverTime` is the shell's server clock reading, so a result's freshness does not depend on the
 * browser's clock.
 */
export function useActionSync(enabled: boolean, serverTime?: string | null) {
  const qc = useQueryClient();
  const base = serverTime ? Date.parse(serverTime) : NaN;
  const anchor = useRef({ base, at: Date.now() });
  if (anchor.current.base !== base && !(Number.isNaN(base) && Number.isNaN(anchor.current.base))) anchor.current = { base, at: Date.now() };
  const serverNow = () => (Number.isFinite(anchor.current.base) ? anchor.current.base + (Date.now() - anchor.current.at) : Date.now());
  useTopicEvents<Event>(enabled ? 'cases' : null, (e) => {
    const x = e.data || {};
    const kind = x.kind || '';
    if (!kind.startsWith('action.') && !kind.startsWith('approval.')) return;
    const id = x.action?.id;
    if (id && kind === 'action.executed') {
      call(api.GET('/actions/{id}', { params: { path: { id } } }))
        .then((a) => settleAction(qc, a, serverNow()))
        .catch(() => refreshDerived(qc));
      return;
    }
    if (id) void qc.invalidateQueries({ queryKey: ['action', id] });
    refreshDerived(qc);
  });
}
